/**
 * Bulk Import — step 2: preview plan with errors, warnings, and op details.
 */

import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/pill';
import { importActionTone } from '../status-tone';
import { Button } from '../../../components/button';
import { ImportPlanResponse, ImportIssue, CohortOpPlan, UserOpPlan, WorldOpPlan, SessionOpPlan, CampaignOpPlan } from '../../../api/admin/import/types';
import admin from '../admin-dashboard.module.css';
import bulk from './bulk-import.module.css';

interface Props {
  plan: ImportPlanResponse;
  fileName: string;
  onChangefile: () => void;
  onExecute: () => void;
  isExecuting: boolean;
}

export function BulkImportPreview({ plan, fileName, onChangefile, onExecute, isExecuting }: Props) {
  const { t } = useTranslation();
  const { summary, cohortOps, userOps, worldOps = [], sessionOps = [], campaignOps = [], errors, warnings } = plan;
  const canExecute = errors.length === 0 && !isExecuting;

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <span>📄 <strong>{fileName}</strong></span>
        <Button type="button" variant="gold" size="sm" onClick={onChangefile}>
          {t('admin.bulkImport.chooseDifferentFile')}
        </Button>
      </div>

      {/* Summary */}
      <h3 className={admin.adminTableTitle}>{t('admin.bulkImport.summary.title')}</h3>
      <div className={bulk.bulkImportSummaryGrid}>
        <SummaryCard title={t('admin.bulkImport.summary.cohortsLine')} create={summary.cohorts.create} update={summary.cohorts.update} del={summary.cohorts.delete} />
        <SummaryCard title={t('admin.bulkImport.summary.usersLine')} create={summary.users.create} update={summary.users.update} del={summary.users.delete} />
        {summary.worlds && <SummaryCard title={t('admin.bulkImport.summary.worldsLine')} create={summary.worlds.create} update={summary.worlds.update} del={summary.worlds.delete} />}
        {summary.sessions && <SummaryCard title={t('admin.bulkImport.summary.sessionsLine')} create={summary.sessions.create} update={summary.sessions.update} del={summary.sessions.delete} />}
        {summary.campaigns && <SummaryCard title={t('admin.bulkImport.summary.campaignsLine')} create={summary.campaigns.create} update={summary.campaigns.update} del={summary.campaigns.delete} />}
        <div className={bulk.bulkImportSummaryCard}>
          <div className={bulk.bulkImportSummaryCardTitle}>{t('admin.bulkImport.summary.assignmentsLine')}</div>
          <div className={bulk.bulkImportSummaryStat}>
            <span>{t('admin.bulkImport.ops.create')}</span>
            <span className={bulk.bulkImportStatAdd}>+{summary.cohortAssignments.add}</span>
          </div>
          <div className={bulk.bulkImportSummaryStat}>
            <span>{t('admin.bulkImport.ops.delete')}</span>
            <span className={bulk.bulkImportStatRemove}>−{summary.cohortAssignments.remove}</span>
          </div>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && <IssueList issues={errors} kind="error" />}

      {/* Warnings */}
      {warnings.length > 0 && <IssueList issues={warnings} kind="warning" />}

      {/* Cohort ops */}
      {cohortOps.length > 0 && (
        <details className={bulk.bulkImportOpsSection}>
          <summary>{t('admin.bulkImport.ops.cohorts.title')} ({cohortOps.length})</summary>
          <table className={bulk.bulkImportOpsTable}>
            <thead>
              <tr>
                <th>{t('admin.bulkImport.ops.action')}</th>
                <th>{t('admin.bulkImport.ops.name')}</th>
                <th>{t('admin.bulkImport.ops.changes')}</th>
              </tr>
            </thead>
            <tbody>
              {cohortOps.map((op, i) => <CohortOpRow key={i} op={op} />)}
            </tbody>
          </table>
        </details>
      )}

      {/* User ops */}
      {userOps.length > 0 && (
        <details className={bulk.bulkImportOpsSection}>
          <summary>{t('admin.bulkImport.ops.users.title')} ({userOps.length})</summary>
          <table className={bulk.bulkImportOpsTable}>
            <thead>
              <tr>
                <th>{t('admin.bulkImport.ops.action')}</th>
                <th>{t('admin.bulkImport.ops.username')}</th>
                <th>{t('admin.bulkImport.ops.changes')}</th>
              </tr>
            </thead>
            <tbody>
              {userOps.map((op, i) => <UserOpRow key={i} op={op} />)}
            </tbody>
          </table>
        </details>
      )}

      {/* World ops */}
      {worldOps.length > 0 && (
        <details className={bulk.bulkImportOpsSection}>
          <summary>{t('admin.bulkImport.ops.worlds.title')} ({worldOps.length})</summary>
          <table className={bulk.bulkImportOpsTable}>
            <thead>
              <tr>
                <th>{t('admin.bulkImport.ops.action')}</th>
                <th>{t('admin.bulkImport.ops.game')}</th>
                <th>{t('admin.bulkImport.ops.name')}</th>
                <th>{t('admin.bulkImport.ops.changes')}</th>
              </tr>
            </thead>
            <tbody>
              {worldOps.map((op, i) => <WorldOpRow key={i} op={op} />)}
            </tbody>
          </table>
        </details>
      )}

      {/* Session ops */}
      {sessionOps.length > 0 && (
        <details className={bulk.bulkImportOpsSection}>
          <summary>{t('admin.bulkImport.ops.sessions.title')} ({sessionOps.length})</summary>
          <table className={bulk.bulkImportOpsTable}>
            <thead>
              <tr>
                <th>{t('admin.bulkImport.ops.action')}</th>
                <th>{t('admin.bulkImport.ops.externalId')}</th>
                <th>{t('admin.bulkImport.ops.world')}</th>
                <th>{t('admin.bulkImport.ops.changes')}</th>
              </tr>
            </thead>
            <tbody>
              {sessionOps.map((op, i) => <SessionOpRow key={i} op={op} />)}
            </tbody>
          </table>
        </details>
      )}

      {/* Campaign ops */}
      {campaignOps.length > 0 && (
        <details className={bulk.bulkImportOpsSection}>
          <summary>{t('admin.bulkImport.ops.campaigns.title')} ({campaignOps.length})</summary>
          <table className={bulk.bulkImportOpsTable}>
            <thead>
              <tr>
                <th>{t('admin.bulkImport.ops.action')}</th>
                <th>{t('admin.bulkImport.ops.name')}</th>
                <th>{t('admin.bulkImport.ops.template')}</th>
                <th>{t('admin.bulkImport.ops.changes')}</th>
              </tr>
            </thead>
            <tbody>
              {campaignOps.map((op, i) => <CampaignOpRow key={i} op={op} />)}
            </tbody>
          </table>
        </details>
      )}

      {/* Actions */}
      <div className="flex gap-2.5 mt-5">
        <Button type="button" variant="gold" onClick={onChangefile}>
          {t('admin.bulkImport.back')}
        </Button>
        <Button type="button" variant={canExecute ? 'primary' : 'gold'} disabled={!canExecute} onClick={onExecute}>
          {isExecuting ? '...' : t('admin.bulkImport.execute')}
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ title, create, update, del }: { title: string; create: number; update: number; del: number }) {
  const { t } = useTranslation();
  return (
    <div className={bulk.bulkImportSummaryCard}>
      <div className={bulk.bulkImportSummaryCardTitle}>{title}</div>
      <div className={bulk.bulkImportSummaryStat}><span>{t('admin.bulkImport.ops.create')}</span><span className={bulk.bulkImportStatCreate}>+{create}</span></div>
      <div className={bulk.bulkImportSummaryStat}><span>{t('admin.bulkImport.ops.update')}</span><span className={bulk.bulkImportStatUpdate}>~{update}</span></div>
      <div className={bulk.bulkImportSummaryStat}><span>{t('admin.bulkImport.ops.delete')}</span><span className={bulk.bulkImportStatDelete}>−{del}</span></div>
    </div>
  );
}

/**
 * The two tones an issue list comes in.
 *
 * A lookup rather than `bulk-import-issues--${kind}`: a module's class names are
 * generated at build time, so a name assembled at runtime resolves to nothing
 * and the list loses its colour without anything failing.
 */
const ISSUE_TONE: Record<'error' | 'warning', string> = {
  error: bulk.bulkImportIssuesError,
  warning: bulk.bulkImportIssuesWarning,
};

function IssueList({ issues, kind }: { issues: ImportIssue[]; kind: 'error' | 'warning' }) {
  const { t } = useTranslation();
  return (
    <div className={`${bulk.bulkImportIssues} ${ISSUE_TONE[kind]}`}>
      <div className={bulk.bulkImportIssuesHeader}>
        {kind === 'error' ? '❌' : '⚠️'}
        {kind === 'error' ? t('admin.bulkImport.errors.title', { count: issues.length }) : t('admin.bulkImport.warnings.title', { count: issues.length })}
      </div>
      <ul className={bulk.bulkImportIssueList}>
        {issues.map((issue, i) => (
          <li key={i} className={bulk.bulkImportIssueItem}>
            <span className={bulk.bulkImportIssueCode}>[{issue.code}]</span>
            {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CohortOpRow({ op }: { op: CohortOpPlan }) {
  return (
    <tr>
      <td><Pill tone={importActionTone(op.action)}>{op.action}</Pill></td>
      <td>
        {op.action === 'update' && op.newName
          ? <><span className={bulk.bulkImportDiffFrom}>{op.name}</span> → <span className={bulk.bulkImportDiffTo}>{op.newName}</span></>
          : op.name
        }
      </td>
      <td>
        {op.diff && Object.entries(op.diff).map(([field, { from, to }]) => (
          <div key={field} className={bulk.bulkImportDiff}>
            <span>{field}: </span>
            <span className={bulk.bulkImportDiffFrom}>{String(from)}</span>
            <span className={bulk.bulkImportDiffTo}>{String(to)}</span>
          </div>
        ))}
        {op.studentCountIfDeleted !== undefined && op.studentCountIfDeleted > 0 && (
          <span className={`${bulk.adminPreviewNote} tone-warn`}>{op.studentCountIfDeleted} students detached</span>
        )}
      </td>
    </tr>
  );
}

function UserOpRow({ op }: { op: UserOpPlan }) {
  return (
    <tr>
      <td><Pill tone={importActionTone(op.action)}>{op.action}</Pill></td>
      <td>
        {op.action === 'update' && op.newUsername
          ? <><span className={bulk.bulkImportDiffFrom}>{op.username}</span> → <span className={bulk.bulkImportDiffTo}>{op.newUsername}</span></>
          : op.username
        }
      </td>
      <td>
        {op.diff && Object.entries(op.diff)
          .filter(([field]) => field !== 'password')
          .map(([field, { from, to }]) => (
            <div key={field} className={bulk.bulkImportDiff}>
              <span>{field}: </span>
              <span className={bulk.bulkImportDiffFrom}>{String(from)}</span>
              <span className={bulk.bulkImportDiffTo}>{String(to)}</span>
            </div>
          ))
        }
        {op.cohortAssignments && (op.cohortAssignments.add.length > 0 || op.cohortAssignments.remove.length > 0) && (
          <div className="text-12 mt-0.5">
            {op.cohortAssignments.add.length > 0 && <span className={bulk.bulkImportDiffTo}>+{op.cohortAssignments.add.join(', ')} </span>}
            {op.cohortAssignments.remove.length > 0 && <span className={bulk.bulkImportDiffFrom}>−{op.cohortAssignments.remove.join(', ')}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}

function WorldOpRow({ op }: { op: WorldOpPlan }) {
  return (
    <tr>
      <td><Pill tone={importActionTone(op.action)}>{op.action}</Pill></td>
      <td>{op.gameName}</td>
      <td>
        {op.action === 'update' && op.newName
          ? <><span className={bulk.bulkImportDiffFrom}>{op.name}</span> → <span className={bulk.bulkImportDiffTo}>{op.newName}</span></>
          : op.name
        }
      </td>
      <td>
        {op.diff && Object.entries(op.diff).map(([field, { from, to }]) => (
          <div key={field} className={bulk.bulkImportDiff}>
            <span>{field}: </span>
            <span className={bulk.bulkImportDiffFrom}>{String(from)}</span>
            <span className={bulk.bulkImportDiffTo}> → {String(to)}</span>
          </div>
        ))}
      </td>
    </tr>
  );
}

function CampaignOpRow({ op }: { op: CampaignOpPlan }) {
  return (
    <tr>
      <td><Pill tone={importActionTone(op.action)}>{op.action}</Pill></td>
      <td>
        {op.action === 'update' && op.newName
          ? <><span className={bulk.bulkImportDiffFrom}>{op.name}</span> → <span className={bulk.bulkImportDiffTo}>{op.newName}</span></>
          : op.name
        }
      </td>
      <td>{op.templateKey ?? '—'}</td>
      <td>
        {op.diff && Object.entries(op.diff).map(([field, { from, to }]) => (
          <div key={field} className={bulk.bulkImportDiff}>
            <span>{field}: </span>
            <span className={bulk.bulkImportDiffFrom}>{String(from)}</span>
            <span className={bulk.bulkImportDiffTo}> → {String(to)}</span>
          </div>
        ))}
        {op.cohortChanges && (op.cohortChanges.add.length > 0 || op.cohortChanges.remove.length > 0) && (
          <div className="text-12 mt-0.5">
            {op.cohortChanges.add.length > 0 && <span className={bulk.bulkImportDiffTo}>+{op.cohortChanges.add.join(', ')} </span>}
            {op.cohortChanges.remove.length > 0 && <span className={bulk.bulkImportDiffFrom}>−{op.cohortChanges.remove.join(', ')}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}

function SessionOpRow({ op }: { op: SessionOpPlan }) {
  return (
    <tr>
      <td><Pill tone={importActionTone(op.action)}>{op.action}</Pill></td>
      <td className="font-mono text-12">{op.externalId}</td>
      <td>{op.gameName} / {op.worldName}</td>
      <td>
        {op.diff && Object.entries(op.diff).map(([field, { from, to }]) => (
          <div key={field} className={bulk.bulkImportDiff}>
            <span>{field}: </span>
            <span className={bulk.bulkImportDiffFrom}>{String(from)}</span>
            <span className={bulk.bulkImportDiffTo}> → {String(to)}</span>
          </div>
        ))}
        {op.cohortChanges && (op.cohortChanges.add.length > 0 || op.cohortChanges.remove.length > 0) && (
          <div className="text-12 mt-0.5">
            {op.cohortChanges.add.length > 0 && <span className={bulk.bulkImportDiffTo}>+{op.cohortChanges.add.join(', ')} </span>}
            {op.cohortChanges.remove.length > 0 && <span className={bulk.bulkImportDiffFrom}>−{op.cohortChanges.remove.join(', ')}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}
